/**
 * Copyright Highway9 Networks Inc.
 * Main-thread bridge to the events WebSocket worker.
 *
 * Custom postMessage events (status, flush) use addEventListener — not worker.onmessage —
 * so Comlink's internal listener for remote.connect/send is not overwritten.
 */
import { Remote, wrap } from "comlink";
import type { EventsSocketWorker } from "./eventsSocketWorker";
import type { CoalescedFlushItem } from "./coalescedEventBatcher";

export type EventsSocketWorkerMessage =
  | { type: "status"; status: "connected" | "reconnecting" }
  /** All object types updated in this 3s window, patches already merged per type. */
  | { type: "flush"; items: CoalescedFlushItem[] };

export type EventsSocketHandlers = {
  onStatus: (status: "connected" | "reconnecting") => void;
  onFlush: (items: CoalescedFlushItem[]) => void;
};

let workerInstance: Worker | undefined;
let remote: Remote<EventsSocketWorker> | undefined;
let handlers: EventsSocketHandlers | undefined;

export function initEventsSocketClient(h: EventsSocketHandlers) {
  handlers = h;
  if (workerInstance) return;

  workerInstance = new Worker(new URL("./eventsSocketWorker", import.meta.url), {
    type: "module",
    name: "events-socket-worker",
  });
  remote = wrap<EventsSocketWorker>(workerInstance);

  // Use addEventListener so Comlink's own message listener is not replaced.
  workerInstance.addEventListener("message", (e: MessageEvent<EventsSocketWorkerMessage>) => {
    const msg = e.data;
    if (!msg || typeof msg !== "object" || typeof msg.type !== "string") return;
    // Ignore Comlink protocol messages (they use numeric ids, not string type).
    if (msg.type === "status") {
      handlers?.onStatus(msg.status);
    } else if (msg.type === "flush") {
      handlers?.onFlush(msg.items);
    }
  });

  workerInstance.onerror = (error) => {
    console.error("Events socket worker error:", error);
  };
}

export function connectEventsSocket(url: string, token: string) {
  if (!remote) {
    throw new Error("Events socket client not initialized");
  }
  return remote.connect(url, token);
}

export function disconnectEventsSocket() {
  return remote?.disconnect();
}

export function sendEventsSocket(message: string) {
  return remote?.send(message);
}

export function terminateEventsSocket() {
  void remote?.terminate();
  workerInstance?.terminate();
  workerInstance = undefined;
  remote = undefined;
}
